import React, { useEffect } from "react";
import {
  Avatar,
  AvatarGroup,
  Box,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

import SignIn from "./SignIn";
import { useSelector } from "react-redux";
import Register from "./Register";
import RegisterConfirm from "./RegisterConfirm";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";

const members = [
  { name: "Aisha M.", color: "#4caf50" },
  { name: "Omar K.", color: "#2196f3" },
  { name: "Sara L.", color: "#ff9800" },
  { name: "Yusuf A.", color: "#e91e63" },
  { name: "Nadia R.", color: "#9c27b0" },
];

const CircleCard = () => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      position: "relative",
      perspective: "1200px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {/* Main card — tilted, centered */}
    <Box
      sx={{
        width: "88%",
        transform: "rotateX(5deg) rotateY(-8deg)",
        transformStyle: "preserve-3d",
        transition: "transform 0.3s ease",
        "&:hover": { transform: "rotateX(2deg) rotateY(-3deg)" },
      }}
    >
      <Box
        sx={{
          bgcolor: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 3,
          p: 3,
          backdropFilter: "blur(12px)",
          boxShadow:
            "0 24px 48px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.7rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                mb: 0.4,
              }}
            >
              Active Circle
            </Typography>
            <Typography
              sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}
            >
              Al-Amal Circle
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.4,
              bgcolor: "rgba(76,175,80,0.2)",
              border: "1px solid rgba(76,175,80,0.4)",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                color: "#81c784",
                fontSize: "0.78rem",
                fontWeight: 600,
                letterSpacing: "0.08em",
              }}
            >
              ROUND 3/12
            </Typography>
          </Box>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography
              sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem" }}
            >
              Monthly savings
            </Typography>
            <Typography
              sx={{ color: "white", fontSize: "0.75rem", fontWeight: 600 }}
            >
              $1,800 / $2,400
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={75}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.1)",
              "& .MuiLinearProgress-bar": {
                bgcolor: "#4caf50",
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Members */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <AvatarGroup
            max={4}
            sx={{
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                fontSize: "0.75rem",
                border: "2px solid rgba(255,255,255,0.15)",
              },
            }}
          >
            {members.map((m) => (
              <Avatar key={m.name} sx={{ bgcolor: m.color }}>
                {m.name[0]}
              </Avatar>
            ))}
          </AvatarGroup>
          <Typography
            sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}
          >
            5 members
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* Floating payout badge — bottom right */}
    <Box
      sx={{
        position: "absolute",
        bottom: "10%",
        right: "2%",
        bgcolor: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 2,
        px: 2,
        py: 1.25,
        backdropFilter: "blur(16px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
      }}
    >
      <Typography
        sx={{
          color: "rgba(255,255,255,0.5)",
          fontSize: "0.78rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        Next payout
      </Typography>
      <Typography sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
        $2,400
      </Typography>
    </Box>

    {/* Floating members badge — top left */}
    <Box
      sx={{
        position: "absolute",
        top: "10%",
        left: "2%",
        bgcolor: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 2,
        px: 2,
        py: 1.25,
        backdropFilter: "blur(16px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
      }}
    >
      <Typography
        sx={{
          color: "rgba(255,255,255,0.5)",
          fontSize: "0.78rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        Total pooled
      </Typography>
      <Typography sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
        $28,800
      </Typography>
    </Box>
  </Box>
);

function LoginPortal() {
  const location = useLocation();
  const navigate = useNavigate();
  const steps = [
    "login",
    "register",
    "confirm",
    "forgot-password",
    "reset-password",
  ];
  const [currentStep, setCurrentStep] = React.useState("login");

  useEffect(() => {
    const path = location.pathname.split("/")[1];
    if (steps.includes(path)) {
      setCurrentStep(path);
    } else {
      setCurrentStep("login");
    }
  }, [location.pathname, steps]);

  const user = useSelector((state) => state.auth.user);
  const isRegister = currentStep === "register";

  return (
    <Box
      sx={{ display: "flex", height: "calc(100vh - 64px)", overflow: "hidden" }}
    >
      {/* ── LEFT PANEL ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: { xs: 0, md: isRegister ? "30%" : "40%" },
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(160deg, #0A2010 0%, #1a5226 55%, #0f3320 100%)",
          transition: "width 0.4s ease-in-out",
          // Subtle leaf texture
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: "url('/leaves.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.07,
            pointerEvents: "none",
          },
          // Subtle grid
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(255,255,255,0.02) 60px), " +
              "repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(255,255,255,0.02) 60px)",
            pointerEvents: "none",
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: "2.5rem",
          }}
        >
          {/* ── Brand lockup ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
            }}
            onClick={() => navigate("/home")}
          >
            <Box
              sx={{
                p: 0.75,
                bgcolor: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalanceIcon sx={{ color: "white", fontSize: 20 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: "1.2rem",
                letterSpacing: "0.01em",
                color: "rgba(255,255,255,0.92)",
              }}
            >
              Sanduuq
            </Typography>
          </Box>

          {/* ── 3D Circle Card ── */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            <CircleCard />
          </Box>

          {/* ── Tagline ── */}
          <Box>
            <Typography
              sx={{
                fontSize: { md: "1.6rem", lg: "1.9rem" },
                fontWeight: 300,
                color: "rgba(255,255,255,0.4)",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              Building Trust,
            </Typography>
            <Typography
              sx={{
                fontSize: { md: "1.6rem", lg: "1.9rem" },
                fontWeight: 700,
                color: "white",
                lineHeight: 1.2,
              }}
            >
              Fueling Dreams.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── RIGHT PANEL ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: isRegister ? "flex-start" : "center",
          bgcolor: "#FAFAFA",
          backgroundImage: "url('/leaves-removebg-preview.png')",
          overflow: "auto",
        }}
      >
        {currentStep === "login" && <SignIn />}
        {currentStep === "register" && <Register />}
        {currentStep === "confirm" && <RegisterConfirm />}
        {currentStep === "forgot-password" && <ForgotPassword />}
        {currentStep === "reset-password" && <ResetPassword />}
      </Box>
    </Box>
  );
}

export default LoginPortal;
